import { draggable } from '@atlaskit/pragmatic-drag-and-drop/element/adapter'
import { setCustomNativeDragPreview } from '@atlaskit/pragmatic-drag-and-drop/element/set-custom-native-drag-preview'
import { motion } from 'motion/react'
import React, { useEffect, useRef } from 'react'
import { flushSync } from 'react-dom'
import { createRoot } from 'react-dom/client'
import { DRAG_TYPES } from '../constants'
import { Card } from './card'

/**
 * Props for the DraggableCard component
 */
export interface DraggableCardProps {
	/** Unique identifier for the candidate */
	candidateId: string
	/** The stage/column this candidate is currently in */
	sourceStage: string
	/** The card content to display */
	children: React.ReactNode
	/** Whether this card is currently selected */
	selected?: boolean
	/** Callback when the card selection is toggled */
	onSelect?: () => void
	/** Callback when the card is clicked */
	onClick?: (e: React.MouseEvent) => void
	/** Whether this card is currently being dragged */
	isDragging?: boolean
	/** Number of selected candidates (for stacked drag preview) */
	selectedCount?: number
	/** Whether dragging is disabled (e.g., during column reorder mode) */
	disabled?: boolean
	/** Accessible label for the selection checkbox */
	selectLabel: string
}

type DragPreviewProps = {
	clone: HTMLElement
	width: number
	height: number
	showStack: boolean
	selectedCount: number
}

function DragPreview({ clone, width, height, showStack, selectedCount }: DragPreviewProps) {
	const mountRef = (node: HTMLDivElement | null) => {
		if (node) { node.appendChild(clone) }
	}

	clone.style.cursor = 'grabbing'
	clone.style.width = `${width}px`

	if (!showStack) {
		clone.style.transform = 'rotate(-3deg)'
		return <div ref={mountRef} />
	}

	clone.style.transform = 'rotate(-2deg)'

	return (
		<div className="relative" style={{ width: width + 8, height: height + 8 }}>
			<div
				className="absolute left-2 top-2 rounded-md border border-purple-300 bg-purple-200"
				style={{ width, height }}
			/>
			<div
				className="absolute left-1 top-1 rounded-md border border-purple-300 bg-purple-100"
				style={{ width, height }}
			/>
			<div ref={mountRef} className="absolute left-0 top-0" />
			<div className="absolute -right-1 -top-2 z-10 rounded-full bg-purple-600 px-2 py-0.5 text-xs font-semibold text-white">
				{selectedCount}
			</div>
		</div>
	)
}

/**
 * DraggableCard Component
 *
 * A card component that can be dragged and dropped between columns in the Kanban board.
 * Wraps the Card component with drag-and-drop functionality using Pragmatic Drag and Drop.
 *
 * Features:
 * - Drag and drop support with smooth animations
 * - Selection support for multi-select operations
 * - Visual feedback during drag operations
 * - Can be disabled during column reordering
 *
 * @example
 * ```tsx
 * <DraggableCard
 *   candidateId="123"
 *   sourceStage="interview"
 *   selected={false}
 *   onSelect={() => handleSelect('123')}
 *   onClick={() => handleClick(candidate)}
 *   selectLabel="Select candidate"
 * >
 *   <CandidateCard candidate={candidate} />
 * </DraggableCard>
 * ```
 */
export function DraggableCard({
	candidateId,
	sourceStage,
	children,
	selected,
	onSelect,
	onClick,
	isDragging,
	selectedCount = 0,
	disabled = false,
	selectLabel,
}: DraggableCardProps) {
	const cardRef = useRef<HTMLDivElement>(null)

	// Setup drag functionality for this card
	useEffect(() => {
		const element = cardRef.current
		if (!element || disabled) { return }

		// Register this element as draggable with Pragmatic Drag and Drop
		return draggable({
			element,
			getInitialData: () => ({
				type: DRAG_TYPES.CANDIDATE,
				candidateId,
				sourceStage,
			}),
			onGenerateDragPreview: ({ nativeSetDragImage, location }) => {
				setCustomNativeDragPreview({
					nativeSetDragImage,
					getOffset: () => {
						// Keep the cursor at the exact position where the user grabbed
						const rect = element.getBoundingClientRect()
						return {
							x: location.current.input.clientX - rect.left,
							y: location.current.input.clientY - rect.top,
						}
					},
					render: ({ container }) => {
						const clone = element.cloneNode(true) as HTMLElement
						const width = element.offsetWidth
						const height = element.offsetHeight
						const showStack = !!(selected && selectedCount > 1)

						const root = createRoot(container)
						flushSync(() => {
							root.render(
								<DragPreview
									clone={clone}
									width={width}
									height={height}
									showStack={showStack}
									selectedCount={selectedCount}
								/>,
							)
						})

						return () => root.unmount()
					},
				})
			},
		})
	}, [candidateId, sourceStage, disabled, selected, selectedCount])

	return (
		<motion.div
			layoutId={`swimlane-${candidateId}-card`}
			initial={{ opacity: 0, scale: 0.8, filter: 'blur(10px)' }}
			animate={{ opacity: 1, scale: 1, filter: 'blur(0px)' }}
			exit={{ opacity: 0, scale: 0.8, filter: 'blur(10px)' }}
			transition={{
				duration: 0.4,
				type: 'spring',
				bounce: 0.2,
				filter: { type: 'tween' },
			}}
			ref={cardRef}
			style={{ width: 'calc(var(--card-width) - 40px)' }}
		>
			<Card
				selected={selected}
				onSelect={onSelect}
				onClick={onClick}
				isDragging={isDragging}
				selectLabel={selectLabel}
			>
				{children}
			</Card>
		</motion.div>
	)
}
